import { Component } from '@theme/component';
import { onDocumentLoaded, requestIdleCallback } from '@theme/utilities';
import { track } from '@theme/r-analytics';

const SOURCE_SELECTOR = 'template[data-r-menu-previews]';
const DESKTOP_HOVER = '(min-width: 990px) and (hover: hover) and (pointer: fine)';

/** Fetches of the preview source per page: the first request and one retry. */
const MAX_ATTEMPTS = 2;

/**
 * Reduce a link to a match key.
 *
 * Both sides are already resolved paths — Liquid resolves a block's `url` setting the
 * same way it renders a menu href — so this only absorbs the trailing-slash and
 * query-string variation a merchant's picked URL can carry against the menu's own. A
 * mismatch here would not throw; the override would just silently never apply.
 *
 * @param {string | null | undefined} raw
 */
function previewKey(raw) {
  const path = (raw ?? '').trim().split('?')[0].split('#')[0].toLowerCase();
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/** @type {Promise<DocumentFragment | null> | null} */
let source = null;

let attempts = 0;

/**
 * Whether the mega menu is the header's live navigation. The media query alone is not
 * enough: a touchscreen laptop has a fine, hover-capable trackpad but is a touch device,
 * so the header puts it in drawer mode (`data-menu-style`, set in layout/theme.liquid and
 * kept current by setHeaderMenuStyle) and the mega menu never shows.
 */
function menuInUse() {
  return (
    matchMedia(DESKTOP_HOVER).matches &&
    document.querySelector('#header-component')?.getAttribute('data-menu-style') === 'menu'
  );
}

/** Whether a load that came back empty may still succeed on a later intent. */
function mayRetry() {
  if (document.querySelector(`header-menu ${SOURCE_SELECTOR}`)) return false;
  return attempts < MAX_ATTEMPTS && Boolean(document.querySelector('header-menu')?.getAttribute('data-r-previews-url'));
}

/* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§7 */
function loadSource() {
  if (source) return source;

  const inline = document.querySelector(`header-menu ${SOURCE_SELECTOR}`);
  if (inline instanceof HTMLTemplateElement) return (source = Promise.resolve(inline.content));

  const url = document.querySelector('header-menu')?.getAttribute('data-r-previews-url');
  if (!url || attempts >= MAX_ATTEMPTS || !menuInUse()) return Promise.resolve(null);

  attempts += 1;
  source = fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`previews: HTTP ${response.status}`, { cause: String(response.status) });
      return response.text();
    })
    .then((html) => {
      const template = new DOMParser().parseFromString(html, 'text/html').querySelector(SOURCE_SELECTOR);
      if (!(template instanceof HTMLTemplateElement) || !template.content.querySelector('.r-menu-preview__layer')) {
        throw new Error('previews: no layers in the response', { cause: 'no-layers' });
      }
      return template.content;
    })
    .catch((error) => {
      // The fallback is quiet (full-width columns), so report it: the 2026-09-21 slice
      // failure was never seen from a desk. `reason` is the HTTP status, 'no-layers'
      // (a challenge or error page answered 200) or 'network'.
      track('slice_miss', { slice: 'previews', reason: error?.cause ?? 'network', attempt: attempts });
      // Let a later intent try again, up to MAX_ATTEMPTS. Panels that asked keep their
      // placeholder until then, and drop the rail only once no attempt is left.
      source = null;
      return null;
    });

  return source;
}

/* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§8 */
function armPrefetch() {
  const nav = document.querySelector('header-menu');
  if (!nav || nav.querySelector(SOURCE_SELECTOR)) return;

  const prefetch = () => {
    if (menuInUse()) loadSource();
  };
  nav.addEventListener('pointerenter', prefetch, { passive: true });
  nav.addEventListener('focusin', prefetch, { passive: true });

  // Idle, after load: waiting for the nav's own pointerenter leaves the first hover
  // waiting on the fetch.
  onDocumentLoaded(() => requestIdleCallback(prefetch, { timeout: 3000 }));
}

/* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§1 */
class MenuPreview extends Component {
  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {HTMLElement | null} */
  #item = null;

  /** @type {HTMLAnchorElement | null} */
  #trigger = null;

  /** @type {Map<string, HTMLElement>} */
  #layers = new Map();

  /** @type {HTMLElement | null} */
  #resting = null;

  /** @type {HTMLElement | null} */
  #active = null;

  #backfilling = false;

  /** @type {Promise<boolean> | null} */
  #adopted = null;

  connectedCallback() {
    super.connectedCallback();

    this.#grid = this.closest('.mega-menu__grid');
    this.#item = this.closest('.menu-list__list-item');
    this.#trigger = this.#item?.querySelector('.menu-list__link') ?? null;

    if (!this.#grid) {
      this.remove();
      return;
    }

    this.#grid.addEventListener('pointerover', this.#onIntent);
    this.#grid.addEventListener('focusin', this.#onIntent);
    this.#item?.addEventListener('pointerenter', this.#onEnter);
    this.#item?.addEventListener('pointerleave', this.#onLeave);
    this.#trigger?.addEventListener('focus', this.#onEnter);

    // The theme editor ships the source inline: adopt now, no fetch, so the merchant
    // sees the rail as soon as the section renders. Everywhere else adoption waits for
    // the first intent, by which time the idle prefetch has usually landed.
    if (document.querySelector(`header-menu ${SOURCE_SELECTOR}`)) this.#ensureAdopted();
  }

  /* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§3 */
  updatedCallback() {
    super.updatedCallback();
    if (!this.#grid || !this.#adopted) return;

    if (!this.#scope()) {
      this.remove();
      return;
    }

    this.#show(this.#active?.isConnected ? this.#active : this.#resting);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cancelBackfill();
    this.#grid?.removeEventListener('pointerover', this.#onIntent);
    this.#grid?.removeEventListener('focusin', this.#onIntent);
    this.#item?.removeEventListener('pointerenter', this.#onEnter);
    this.#item?.removeEventListener('pointerleave', this.#onLeave);
    this.#trigger?.removeEventListener('focus', this.#onEnter);
    this.#grid = this.#item = this.#trigger = null;
    this.#layers.clear();
  }

  /* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§9 */
  #ensureAdopted() {
    if (this.#adopted) return this.#adopted;

    this.#adopted = loadSource().then((fragment) => {
      if (!this.isConnected) return false;

      if (!fragment && mayRetry()) {
        // Not yet rather than never: the window is below the desktop range right now, or
        // this attempt failed and one is left. Keep the placeholder and let the next
        // intent ask again — a panel hovered at 960px still gets its rail at 1440.
        this.#adopted = null;
        return false;
      }

      const stage = this.querySelector('.r-menu-preview__stage');
      if (!fragment || !stage) {
        // Nothing to show: every attempt failed, or the store has no images at all. Hand
        // the panel back its full-width columns rather than leave an empty frame.
        this.remove();
        return false;
      }

      stage.querySelector('[data-r-preview-placeholder]')?.remove();
      for (const layer of fragment.querySelectorAll('.r-menu-preview__layer')) {
        stage.append(layer.cloneNode(true));
      }

      if (!this.#scope()) {
        this.remove();
        return false;
      }

      return true;
    });

    return this.#adopted;
  }

  /**
   * Index every layer under each link URL it answers for, dropping what this panel cannot
   * reach, and pick the one that rests.
   *
   * The first claim on a key wins, and merchant overrides are emitted before the derived
   * layers, so overrides take precedence without needing a marker.
   *
   * Resting is the layer answering for the menu's own top-level link, else the first
   * surviving layer.
   *
   * @returns {boolean} whether any layer survived
   */
  #scope() {
    this.#layers.clear();

    const reachable = new Set();
    for (const link of this.#grid?.querySelectorAll('a[href]') ?? []) {
      reachable.add(previewKey(link.getAttribute('href')));
    }
    // The trigger sits outside the grid, but the panel still has a layer for it.
    const triggerKey = previewKey(this.#trigger?.getAttribute('href'));
    if (triggerKey) reachable.add(triggerKey);

    let first = null;

    for (const layer of [...this.querySelectorAll('.r-menu-preview__layer')]) {
      let claimed = 0;

      for (const raw of (layer.getAttribute('data-r-preview-link') ?? '').split(/\s+/)) {
        const key = previewKey(raw);
        if (!key || this.#layers.has(key) || !reachable.has(key)) continue;
        this.#layers.set(key, layer);
        claimed += 1;
      }

      if (claimed === 0) layer.remove();
      else if (!first) first = layer;
    }

    this.#resting = this.#layers.get(triggerKey) ?? first;

    return this.#layers.size > 0;
  }

  /**
   * Pointer or keyboard focus landing on a link inside the panel. Both events are handled
   * identically, which gives the rail keyboard parity for free: tabbing through the panel
   * walks the preview exactly as hovering does.
   * @param {Event} event
   */
  #onIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!link || !this.#grid?.contains(link)) return;

    this.#ensureAdopted().then((ready) => {
      if (!ready) return;

      const layer = this.#layers.get(previewKey(link.getAttribute('href')));
      if (!layer) return;

      this.#load(layer, 'high');
      this.#scheduleBackfill();
      this.#show(layer);
    });
  };

  /* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§4 */
  #onEnter = () => {
    this.#ensureAdopted().then((ready) => {
      if (!ready) return;

      this.#load(this.#resting, 'high');
      this.#show(this.#resting);
      this.#scheduleBackfill();
    });
  };

  /* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§5 */
  #onLeave = () => {
    this.#cancelBackfill();
  };

  /** @param {HTMLElement | null} layer */
  #show(layer) {
    if (!layer || layer === this.#active) return;

    this.#active?.removeAttribute('data-r-preview-active');
    layer.setAttribute('data-r-preview-active', '');
    this.#active = layer;
  }

  /**
   * Clone one layer's parked <template> into its frame, which is what actually starts the
   * image request. Idempotent: the template is removed as it is used, so a layer already
   * loaded is a no-op.
   *
   * The markup ships inert because `loading="lazy"` does not defer these: the panel is
   * `visibility: hidden`, which still lays out, and those boxes are inside the viewport —
   * so without the template every image is fetched on every page load, for a menu most
   * visitors never open. See snippets/r-menu-preview-layer.liquid.
   *
   * @param {HTMLElement | null} layer
   * @param {'high' | 'low'} priority
   * @returns {boolean} whether this call started a load
   */
  #load(layer, priority) {
    const template = layer?.querySelector('template.r-menu-preview__source');
    if (!(template instanceof HTMLTemplateElement)) return false;

    const frame = template.parentElement;
    template.remove();
    frame?.append(template.content.cloneNode(true));

    const image = frame?.querySelector('img');
    if (image instanceof HTMLImageElement) {
      // Already in the DOM and wanted, so lazy would only add an extra viewport check.
      image.loading = 'eager';
      // Backfilled images must not compete with anything the visitor is actually looking
      // at — including the page behind the menu.
      image.fetchPriority = priority;
    }

    return true;
  }

  /* ↗ .cursor/references/code-notes/r-menu-preview.js.md#§6 */
  #scheduleBackfill() {
    if (this.#backfilling) return;
    this.#backfilling = true;

    const step = () => {
      if (!this.#backfilling || !this.isConnected) return;

      const template = this.querySelector('template.r-menu-preview__source');
      if (!template) {
        this.#backfilling = false;
        return;
      }

      this.#load(template.closest('.r-menu-preview__layer'), 'low');
      requestIdleCallback(step, { timeout: 2000 });
    };

    requestIdleCallback(step, { timeout: 2000 });
  }

  #cancelBackfill() {
    this.#backfilling = false;
  }
}

if (!customElements.get('r-menu-preview')) {
  customElements.define('r-menu-preview', MenuPreview);
}

armPrefetch();
